import { usePasswordFeedback } from '@/hooks';
import {
  Icon,
  List,
  ListItem,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Progress,
  Text,
} from '@chakra-ui/react';
import type { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MdDoneOutline } from 'react-icons/md';

interface IPasswordFeedbackProps {
  children: React.ReactNode;
  error?: FieldError;
  isOpen: boolean;
  onClose: () => void;
  value: string;
}

export const PasswordFeedback: React.FC<IPasswordFeedbackProps> = ({
  children,
  error,
  isOpen,
  onClose,
  value,
}): JSX.Element => {
  const { t } = useTranslation();
  const { errors: pswFeedbackErrors, getMessageColor, getProgressProps } = usePasswordFeedback();
  return (
    <Popover
      arrowSize={16}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={false}
      gutter={16}
      isOpen={isOpen}
      matchWidth
      placement="bottom-start"
      returnFocusOnClose={false}
      onClose={onClose}
    >
      <PopoverTrigger>{children}</PopoverTrigger>

      <PopoverContent border={0} boxShadow="0 2px 8px 2px rgba(53, 60, 79, 0.1)" m={0} w="full">
        <PopoverArrow />
        <PopoverBody color="#666" px={8} py={6}>
          <Text fontSize="medium" mb={2}>
            {t('FORM.PSW_FEEDBACK_LABEL')}
          </Text>
          <List fontSize="small">
            {pswFeedbackErrors.map(({ errorType, message }, index) => (
              <ListItem
                alignItems="center"
                color={getMessageColor(error?.types?.[errorType])}
                display="flex"
                key={index}
                mb={2}
              >
                <Icon as={MdDoneOutline} h={4} mr={2} w={4} />
                {t(message)}
              </ListItem>
            ))}
          </List>
          <Text fontSize="medium" mb={2} textTransform="uppercase">
            {t('FORM.PASSWORD_STRENGTH')}
          </Text>
          <Progress rounded={5} {...getProgressProps(value, error?.types)} />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
